import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Message, Spin, Statistic, Tag } from '@arco-design/web-react';

import { useWsChargeOverview } from '@/auth-conf/cost-center/chargeOverview';
import { useBillingManagement, useOrderManagement, useRechargeRecords } from '@/auth-conf/order/orderManagement';
import { PermissionSyncWrapper, usePermissionSync } from '@/components/PermissionSync';
import { GROUPS } from '@/service/groups';
import { formatAmount } from '@/utils/format';
import useUrlParams, { usePathTo } from '@/utils/useUrlParams';

import PayModal from './payModal';
import { getSummary } from './service';

// 样式常量
const CONTAINER_STYLES = {
  display: 'flex',
  gap: 16,
} as const;

const CARD_STYLES = {
  flex: 1,
  padding: 32,
  borderRadius: 4,
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  fontSize: 16,
  fontWeight: 500,
  justifyContent: 'space-between',
} as const;

const WALLET_CONTAINER_STYLES = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
} as const;

const TAG_CONTAINER_STYLES = {
  margin: '0 32px 32px',
  display: 'flex',
  gap: 12,
} as const;

const METRICS_CONTAINER_STYLES = {
  display: 'flex',
  flex: 1,
  gap: 16,
} as const;

const STATISTIC_VALUE_STYLE = {
  fontSize: 16,
  fontWeight: 500,
} as const;

const WALLET_BALANCE_SECTION_STYLES = {
  padding: '32px 32px 0',
} as const;

const BALANCE_MAIN_ROW_STYLES = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 16,
  fontWeight: 500,
} as const;

const BALANCE_SUB_ROW_STYLES = {
  marginTop: 8,
  marginBottom: 16,
  fontSize: 12,
  color: '#999',
  fontWeight: 400,
  display: 'flex',
  gap: 20,
} as const;

const formatStatisticTitle = (text: string) => text.replace(/[：:]\s*$/, '');

const formatStatisticAmount = (value: string | number) => formatAmount({ amount: value.toString() });

// 类型定义
interface SummaryData {
  dataCenter: string;
  workspace: string;
  totalConsumption: string;
  outstandingPayment: string;
  currency: string;
  totalConsumptionYoy: string;
  outstandingPaymentYoy: string;
  balance: string;
  creditLimit: string;
  analysisPeriod: string;
  startTime: string;
  endTime: string;
}

const Summary = () => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<SummaryData>();
  const navigate = usePathTo();
  const { getParam, setParam } = useUrlParams();
  const pay = getParam('pay');

  const wsChargeOverviewMap = useWsChargeOverview();
  const orderAuthMap = useOrderManagement();
  const billAuthMap = useBillingManagement();
  const rechargeAuthMap = useRechargeRecords();
  // 页内订单/账单/充值记录入口复用独立菜单权限，不挂在总览下
  const authMap = useMemo(
    () => [wsChargeOverviewMap, orderAuthMap, billAuthMap, rechargeAuthMap] as any,
    [wsChargeOverviewMap, orderAuthMap, billAuthMap, rechargeAuthMap],
  );

  const { hasPermission: canRecharge } = usePermissionSync({
    permission: { group: GROUPS.ophub, resource: 'recharge-requests', type: 'create' },
    authMap,
  });

  const availableBalance = useMemo(() => {
    const balance = parseFloat(String(summaryData?.balance ?? '0'));
    const creditLimit = parseFloat(String(summaryData?.creditLimit ?? '0'));
    if (isNaN(balance) || isNaN(creditLimit)) return 0;
    return balance + creditLimit;
  }, [summaryData?.balance, summaryData?.creditLimit]);

  useEffect(() => {
    if (pay === '1' && canRecharge) {
      setIsModalVisible(true);
    }
  }, [pay, canRecharge]);

  // 获取数据
  const fetchSummaryData = async () => {
    setIsLoading(true);
    try {
      const response = await getSummary();
      setSummaryData(response);
    } catch (error) {
      console.error('获取账户总览数据失败:', error);
      Message.error(t('common:failed.to.obtain.account.overview.data.please.try.again.later'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryData();
  }, []);

  // 处理支付成功
  const handlePaySuccess = () => {
    setIsModalVisible(false);
    fetchSummaryData(); // 刷新数据
    setParam('pay', undefined);
  };

  // 处理导航点击
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // 打开充值模态框
  const handleOpenRechargeModal = () => {
    setIsModalVisible(true);
  };

  // 关闭充值模态框
  const handleCloseRechargeModal = () => {
    setIsModalVisible(false);
    setParam('pay', undefined);
  };

  return (
    <Spin loading={isLoading} block>
      <div style={CONTAINER_STYLES}>
        {/* 钱包余额区域 */}
        <div style={WALLET_CONTAINER_STYLES}>
          <div style={WALLET_BALANCE_SECTION_STYLES}>
            <div style={BALANCE_MAIN_ROW_STYLES}>
              <span>
                {t('tenant:account.balance')}
                {formatAmount({ amount: summaryData?.balance })}
              </span>
              <PermissionSyncWrapper permission={{ group: GROUPS.ophub, resource: 'recharge-requests', type: 'create' }} authMap={authMap}>
                <Button style={{ fontWeight: 500 }} type="primary" onClick={handleOpenRechargeModal}>
                  {t('order:recharge.now')}
                </Button>
              </PermissionSyncWrapper>
            </div>
            <div style={BALANCE_SUB_ROW_STYLES}>
              <span>
                {t('tenant:credit.balance')}
                {formatAmount({ amount: summaryData?.creditLimit })}
              </span>
              <span>
                {t('tenant:available.balance')}
                {formatAmount({ amount: availableBalance })}
              </span>
            </div>
          </div>

          {/* 导航标签区域 */}
          <div style={TAG_CONTAINER_STYLES}>
            <PermissionSyncWrapper permission={{ group: GROUPS.ophub, resource: 'order-items', type: 'list' }} authMap={authMap}>
              <Tag
                color="rgb(var(--primary-6))"
                bordered
                style={{ cursor: 'pointer', padding: '4px 12px' }}
                onClick={() => handleNavigation('/orders')}
              >
                {t('common:order.list')}
              </Tag>
            </PermissionSyncWrapper>
            <PermissionSyncWrapper permission={{ group: GROUPS.ophub, resource: 'invoices', type: 'list' }} authMap={authMap}>
              <Tag
                color="rgb(var(--primary-6))"
                bordered
                style={{ cursor: 'pointer', padding: '4px 12px' }}
                onClick={() => handleNavigation('/bills')}
              >
                {t('common:bill.list')}
              </Tag>
            </PermissionSyncWrapper>
            <PermissionSyncWrapper permission={{ group: GROUPS.ophub, resource: 'recharge-requests', type: 'list' }} authMap={authMap}>
              <Tag
                color="rgb(var(--primary-6))"
                bordered
                style={{ cursor: 'pointer', padding: '4px 12px' }}
                onClick={() => handleNavigation('/recharge')}
              >
                {t('common:recharge.records')}
              </Tag>
            </PermissionSyncWrapper>
          </div>
        </div>

        {/* 消费指标区域 */}
        <div style={METRICS_CONTAINER_STYLES}>
          <div style={CARD_STYLES}>
            <Statistic
              title={formatStatisticTitle(t('tenant:total.consumption.amount'))}
              value={summaryData?.totalConsumption ?? '0'}
              renderFormat={formatStatisticAmount}
              styleValue={STATISTIC_VALUE_STYLE}
              precision={0}
            />
          </div>
          <div style={CARD_STYLES}>
            <Statistic
              title={formatStatisticTitle(t('tenant:total.pending.payment.amount'))}
              value={summaryData?.outstandingPayment ?? '0'}
              renderFormat={formatStatisticAmount}
              styleValue={STATISTIC_VALUE_STYLE}
            />
          </div>
        </div>
      </div>

      {/* 充值模态框 */}
      <PayModal
        visible={isModalVisible}
        onCancel={handleCloseRechargeModal}
        onOk={handlePaySuccess}
        balance={formatAmount({ amount: summaryData?.balance })}
      />
    </Spin>
  );
};

export default Summary;
